import React, { useCallback, useState, useEffect, useRef } from "react";
import Iframe from "react-iframe";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";
import Loader from "react-loader-spinner";
import i18n from "../../../../i18n";
// Components
import { Container, Row, Col } from "react-bootstrap";
import Progress from "../../Progress";
import { Error, ErrorInPage } from "./../../../common/index";
import { Spinner } from "../../../common/spinner";
import { Formik } from "formik";
import { TxButton, TxButtonMode } from "texkit-ui";
import { TxPaymentCardForm, TxPaymentCardFormMode } from "texkit-ui/forms";

// Navigation
import history from "./../../../../components/common/history";

// Hooks
import useEventListener from "./../../UseEventListener";
import useGlobal from "../../../../store";

// Styles
import "react-datepicker/dist/react-datepicker.css";
import "./Payment.scss";

// Requests
import GetItemizedTotal from "../../../../api/GetItemizedTotal";
import GetAmountDistribution from "../../../../api/AmountDistribution";
import GetCheckout from "../../../../api/GetCheckout";
import GetSubmitPayment from "../../../../api/GetSubmitPayment";
import GetPublicIPAddress from "../../../../api/GetPublicIPAddress";

// Analytics
import { eventGA } from "./../../../common/GoogleAnalytics";

function Payment(props) {
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const [paymentUrl, setPaymenturl] = useState("");
  const [paymentToken, setPaymentToken] = useState("");
  const [showError, setShowError] = useState(false);
  const [showInpageError, setShowInpageError] = useState(false);
  const [errorReference, setErrorReference] = useState("");
  const [paymentError, setPaymentError] = useState(false);
  const [paymentCallInProgress, setPaymentCallInProgress] = useState(false);
  const [iFrameLoading, setIFrameLoading] = useState(false);
  const applicationId = "OVRA";
  const agencyId = "DSHS";
  const uniqueIdentifier = globalState.uniqueIdentifier;
  const [orderHasFetalDeathCert, setOrderHasFetalDeathCert] = useState(false);
  /*PRJ1185082, STRY0262644 - added these as part of new paymentForm Integration */
  const acceptedCards = ["Visa", "MasterCard", "Discover", "American Express"];
  const stylecss = '{"height":"52px", "background-color":"transparent"}';
  const [cardData, setCardData] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitButtonState, setSubmitButtonState] = useState(false);

  const initialValues = {
    name: "",
    expirationDate: "",
    cvv: "",
    streetAddress: "",
    streetAddress2: "",
    city: "",
    state: "",
    zip: "",
  };

  // Reset checkbox when fields become untouched (this is rare but handles edge cases)
  useEffect(() => {
    // This effect is minimal since we only care about touched state now
  }, []);

  const checkoutObject = (ip) => {
    const checkOutDetailsRequestObject = {
      applicationId,
      agencyId,
      uniqueIdentifier,
      inTransactionId: "0",
      totalAmount: JSON.parse(sessionStorage.getItem("paymentItem"))
        .totalAmount,
      orderNumber: globalState.orderId,
      transactionStatus: "NOT PAID",
      language: i18n.language,
      paymentMode: "CC",
      stylecss: stylecss,
      checkoutConfiguration: {
        host: ip,
      },
    };
    return checkOutDetailsRequestObject;
  };
  const hideError = () => {
    setShowInpageError(false);
  };
  const hidePaymentError = () => {
    setPaymentError(false);
  };
  setTimeout(() => {
    setIFrameLoading(false);
  }, 2000);

  const publicIPAddressRef = useRef();
  useEffect(() => {
    GetPublicIPAddress(
      uniqueIdentifier,
      applicationId,
      agencyId,
      paymentToken
    ).then((resp) => {
      publicIPAddressRef.current = resp;
    });
  }, [uniqueIdentifier, paymentToken]);

  // ORDER FEE CALCULATION
  const GetItemizedTotalMethod = () => {
    let orderId = globalState.orderId;
    sessionStorage.setItem("orderId", orderId);
    trackPromise(
      GetItemizedTotal(orderId, globalState.uniqueIdentifier).then(
        (response) => {
          globalActions.addItemizedTotalPayment(response.data);
          sessionStorage.setItem("paymentItem", JSON.stringify(response.data));
          // AMOUNT DISTRIBUTION CALL
          trackPromise(
            GetAmountDistribution(
              sessionStorage.getItem("orderId"),
              globalState.uniqueIdentifier
            ).then((response) => {
              globalActions.addAmountDistribution(response.data);
              // CHECKOUT CALL
              if (publicIPAddressRef.current !== "") {
                let checkoutRequest = checkoutObject(
                  publicIPAddressRef.current
                );
                setIFrameLoading(true);
                GetCheckout(checkoutRequest)
                  .then((response) => {
                    globalActions.addCheckOutOrder(response.data);
                    setIFrameLoading(false);
                    setPaymenturl(response.data.url);
                    setPaymentToken(response.data.paymentToken);
                  })
                  // CHECKOUT ERROR CATCH
                  .catch(function (error) {
                    setErrorReference(JSON.stringify(error));
                    setShowError(true);
                    setIFrameLoading(false);
                  });
              } else {
                setShowError(true);
              }
            })
          )
            // AMOUNT DISTRIBUTION ERROR CATCH
            .catch(function (error) {
              setErrorReference(JSON.stringify(error));
              setShowError(true);
            });
        }
      )
    )
      // ORDER ERROR CATCH
      .catch(function (error) {
        setErrorReference(JSON.stringify(error));
        setShowError(true);
      });
  };
  useEffect(() => {
    if (globalState.orderId !== undefined && globalState.orderId !== "") {
      GetItemizedTotalMethod();
    } else {
      history.push(t("URLS.SELECT_VITAL_RECORD"));
    }
  }, []);
  const onSuccess = (values, formikProps) => {
    if (paymentCallInProgress === false) {
      //PRJ1185082, DFCT0035726 - added to disable submit button to avoid multiple clicks
      setSubmitButtonState(true);
      setPaymentCallInProgress(true);
      setPaymentError(false);
      window.scrollTo(0, 0);
      trackPromise(
        GetSubmitPayment(
          cardData.token,
          globalState.amountDistribution.usasCodes,
          globalState.checkOutOrder.outTransactionId,
          globalState.checkOutOrder.traceNumber,
          globalState.uniqueIdentifier,
          publicIPAddressRef.current,
          values.name,
          cardData.cardtype,
          values.cvv,
          cardData.token ? cardData.token.slice(-4) : "",
          values.expirationDate.replace("/", ""),
          values.streetAddress,
          values.streetAddress2,
          values.city,
          values.state,
          values.zip,
          globalState.amountDistribution.usasLines
        )
      )
        .then((response) => {
          setPaymentCallInProgress(false);
          //Google Analytics- Send Payment Event
          eventGA(
            "OVRA",
            "Payment",
            "orderID: " +
              globalState.orderId +
              " traceNumber: " +
              globalState.checkOutOrder.traceNumber,
            1,
            true
          );
          props.nextCallback
            ? props.nextCallback()
            : history.push("./order-receipt");
        })
        .catch(function (error) {
          if (error.message.includes("500")) {
            setShowError(true);
            formikProps.setSubmitting(false);
            return;
          } else {
            setPaymentCallInProgress(false);
            setPaymentError(true);
            formikProps.setSubmitting(false);
          }

          window.scrollTo(0, document.body.scrollHeight);
          // PAYMENT RESUBMISSION IFRAME ON FAILURE
          if (publicIPAddressRef.current !== "") {
            //PRJ1185082, DFCT0035726 - added to disable submit button to avoid multiple clicks
            setSubmitButtonState(false);
            setIFrameLoading(true);
            let checkoutRequest = checkoutObject(publicIPAddressRef.current);
            trackPromise(
              GetCheckout(checkoutRequest).then((response) => {
                globalActions.addCheckOutOrder(response.data);
                setPaymenturl(response.data.url);
                setIFrameLoading(false);
                setPaymentToken(response.data.paymentToken);
              })
            ).catch(function (error) {
              //PRJ1185082, DFCT0035726 - added to disable submit button to avoid multiple clicks
              setSubmitButtonState(false);
              setIFrameLoading(false);
              setErrorReference(JSON.stringify(error));
              formikProps.setSubmitting(false);
              setShowError(true);
            });
          } else {
            //PRJ1185082, DFCT0035726 - added to disable submit button to avoid multiple clicks
            setSubmitButtonState(false);
            setShowError(true);
            setIFrameLoading(false);
            formikProps.setSubmitting(false);
          }
        });
    }
  };
  useEffect(() => {
    if (globalState.totalOrderRecords) {
      globalState.totalOrderRecords.map((cert) => {
        if (
          (cert.certificateInfoType &&
            cert.certificateInfoType === "stillbirthDeath") ||
          cert.certificateInfoType === "fetalDeath"
        ) {
          setOrderHasFetalDeathCert(true);
          return;
        }
      });
    }
  }, []);
  const onCardFieldMessage = (data) => {
    setCardData(data);
  };

  //PRJ1185082,STRY0263630 - Check if all required fields are touched to enable submit button
  const allFieldsTouched = useCallback((touchedMap, values) => {
    const allFields = Object.keys(values).filter(
      (field) => field !== "streetAddress2"
    );
    return allFields.every((field) => touchedMap[field]);
  }, []);
  //PRJ1185082,STRY0263630 - marks single field as touched when blurred
  const handleFieldBlur = (event, formikProps) => {
    const fieldName = event.target.name;
    formikProps.setFieldTouched(fieldName, true);
  };
  /* PRJ1185082,DFCT0035728 - Checkbox to enable submit button */
  const handleFieldChange = (event, formikProps) => {
    const fieldName = event.target.name;
    const fieldValue = event.target.value;
    const currentFieldValue = formikProps.values[fieldName];

    // Call the original handleChange
    formikProps.handleChange(event);

    // Mark field as touched if it has content
    if (fieldValue && fieldValue.trim() !== "") {
      formikProps.setFieldTouched(fieldName, true);
    }
  };

  //PRJ1185082,STRY0265334 - Handling Form Validation logic
  const onFormError = (errors) => {
    if (Object.keys(errors).length > 0) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };
  return (
    <>
      {!showError && (
        <Container className="payment-review-container">
          <Progress currentStep={4} totalSteps={4} />
          <Spinner />
          <h1 className="payment-review-heading">{t("PAYMENT.HEADING")}</h1>
          <label className="payment-desc">
            {t("PAYMENT.DESC")}
            <span className="payment-warning">
              {t("PAYMENT.DESC_CONTINUATION")}
            </span>{" "}
            {t("PAYMENT.DESC_CONTINUATION_2")}
            <br />
            <br />
            {orderHasFetalDeathCert && (
              <>
                <span className="payment-warning">{t("PAYMENT.DESC")}</span>
                {t("PAYMENT.DESC_CONTINUATION_3")}
              </>
            )}
          </label>
          <h2 className="payment-sub-heading">{t("PAYMENT.ORDER_FEE")}</h2>
          <div className="itemized-total-section">
            <Row className="payment-order-sub-total">
              <Col className="payment-font-style">
                <label>{t("PAYMENT.RECORD_SUB_TOTAL")}</label>
              </Col>
              <Col className="payment-sub-amount">
                {" "}
                {globalState.paymentItemizedTotal.certificateFee && (
                  <p>
                    $
                    {(
                      parseFloat(
                        globalState.paymentItemizedTotal.certificateFee
                      ) +
                      parseFloat(
                        globalState.paymentItemizedTotal.convenienceFee
                      )
                    ).toFixed(2)}
                  </p>
                )}{" "}
              </Col>
            </Row>

            {!globalState.isOnlineOrder && (
              <Row className="payment-order-sub-total-2">
                <Col className="payment-font-style">
                  <label>{t("PAYMENT.DONATIONS")}</label>
                </Col>
                <Col className="payment-sub-amount">
                  {" "}
                  <p>${globalState.paymentItemizedTotal.donationAmount}</p>{" "}
                </Col>
              </Row>
            )}

            <Row className="payment-order-sub-total">
              {/* STRY0172062 - Display Shipping Type on Payment page along with Shipping Fee*/}
              <Col className="payment-font-style">
                <label>
                  {t("PAYMENT.SHIPPING_FEE")}{" "}
                  {!globalState.isOnlineOrder &&
                  globalState.paymentItemizedTotal.shippingType ? (
                    <span>
                      (
                      {t(
                        "COMMONS." +
                          globalState.paymentItemizedTotal.shippingType
                            .toUpperCase()
                            .replace(" ", "_")
                      )}
                      )
                    </span>
                  ) : (
                    ""
                  )}
                </label>
              </Col>
              <Col className="payment-sub-amount">
                {" "}
                <p>${globalState.paymentItemizedTotal.shippingAmount}</p>{" "}
              </Col>
            </Row>
            <Row className="payment-order-sub-total-2">
              <Col>
                <label className="payment-font-style-total">
                  {t("PAYMENT.TOTAL")}
                </label>
              </Col>
              <Col className="payment-sub-amount total-color">
                {" "}
                <p>${globalState.paymentItemizedTotal.totalAmount}</p>{" "}
              </Col>
            </Row>
          </div>
          {iFrameLoading && (
            <div className="spinner">
              <Loader
                type="ThreeDots"
                color="#007bff"
                height="100"
                width="100"
              />
            </div>
          )}
          {/* IFRAME */}
          <div
            className={
              paymentCallInProgress
                ? "iframe-align iframe-hide"
                : "iframe-align "
            }
          >
            {/*PRJ1185082, STRY0262644 - Integration of new Payment form */}
            {paymentUrl !== "" && !iFrameLoading && (
              <Formik
                onSubmit={onSuccess}
                validateOnBlur={false}
                validateOnChange={false}
                initialValues={initialValues}
              >
                {(formikProps) => {
                  const { values, touched, isSubmitting } = formikProps;
                  const allFieldsAreTouched = allFieldsTouched(touched, values);
                  return (
                    <form onSubmit={formikProps.handleSubmit} noValidate>
                      <TxPaymentCardForm
                        iFrameUrl={paymentUrl}
                        billingAddressSubTitle={t(
                          "PAYMENT.BILLING_ADDRESS_SUB_TITLE"
                        )}
                        billingAddressTitle={t("PAYMENT.BILLING_ADDRESS_TITLE")}
                        paymentMethodSubTitle=""
                        paymentMethodTitle={
                          <h1 className="payment-review-heading">
                            {t("PAYMENT.PAYMENT_TITLE")}
                          </h1>
                        }
                        acceptedCards={acceptedCards}
                        onCardFieldMessage={onCardFieldMessage}
                        mode={TxPaymentCardFormMode.TransactionAdd}
                        spanish={i18n.language === "es"}
                        onFieldBlur={(event) =>
                          handleFieldBlur(event, formikProps)
                        }
                        onFieldChange={(event) =>
                          handleFieldChange(event, formikProps)
                        }
                        values={values}
                        onFormError={onFormError}
                      />
                      <TxButton
                        mode={TxButtonMode.Tertiary}
                        type="submit"
                        className="submit-btn"
                        disabled={
                          !isFormValid ||
                          !allFieldsAreTouched ||
                          isSubmitting ||
                          !cardData.token ||
                          submitButtonState
                        }
                      >
                        {t("PAYMENT.SUBMIT")}
                      </TxButton>
                    </form>
                  );
                }}
              </Formik>
            )}
          </div>
          {paymentError && (
            <ErrorInPage
              title={t("PAYMENT.PAYMENT_ERROR_TITLE")}
              //PRJ1185082, DFCT0035477 - Add uniqueidentifier to the payment error
              description={
                <>
                  {t("PAYMENT.PAYMENT_ERROR_DESC")}
                  <p className="error-page-content">
                    {t("ERROR.ERROR_REFERENCE")} {globalState.uniqueIdentifier}
                  </p>
                </>
              }
              hideCallBack={hidePaymentError}
            />
          )}
          {showInpageError && (
            <ErrorInPage
              title={t("PAYMENT.ERROR_TITLE")}
              description={t("PAYMENT.ERROR_DESCRIPTION")}
              hideCallBack={hideError}
            />
          )}
        </Container>
      )}
      <div className="bottom-height-adjust"></div>
      {showError && <Error errorReference={globalState.uniqueIdentifier} />}
    </>
  );
}
export default Payment;
